import { useState } from 'react'

import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Button from 'components/forms/Button'
import NavDropdown from 'components/navbar/NavDropdown'
import { setMode } from 'store/login-modal/actions'
import { toggleModal, logout } from 'store/ui/actions'
import styles from './styles/NavLogin.css'
import HoverLink from './HoverLink'
import NavAvatar from '../misc/NavAvatar'
import CircleSpinner from '../misc/CircleSpinner'
import classNames from '../../../node_modules/classnames/index'
import { getLocalStorage } from '../../lib/get-local-storage'

const NavLogin = ({
  className = '',
  avatarBg = 'gray',
  buttonColor = 'transparent',
  showSignup,
  hideAuthButtons = false,
  signupClassName = '',
  loginClassName = '',
  avatarWrapperMobile = '',
  navBarButtons = ''
}) => {
  const user = useSelector(({ ui: { user } }) => user)
  const organizationLicense = useSelector(({ ui }) => ui.organizationLicense)
  const fetchingUser = useSelector(({ ui: { fetchingUser } }) => fetchingUser)

  const dispatch = useDispatch()
  const history = useHistory()
  const [hasToken] = useState(() => {
    const localStorage = getLocalStorage()
    return !!localStorage?.getItem('apiToken')
  })

  // Mirror the standard sidebar user menu (SidebarUserSection) so both menus
  // offer the same options + @central icons, with "My Profile" added on top.
  const setAvatarItems = () =>
    [
      {
        name: (
          <>
            <span className={styles.navUserName}>{user.name}</span>
            <span className={styles.navUserEmail}>{user.email}</span>
          </>
        ),
        href: ''
      },
      {
        name: 'My Profile',
        icon: 'user',
        href: `/users/${user.slug}`
      },
      {
        name: 'Subscription',
        icon: 'subscription',
        href: `/users/${user.slug}/subscription`
      },
      {
        name: 'Account Settings',
        icon: 'accountSettings',
        href: `/users/${user.slug}/account-settings`
      },
      // Only the team's admin (in the license admin_contacts) can manage it —
      // not every member who now holds the `team` plan.
      organizationLicense?.isTeamAdmin && {
        name: 'Manage Team',
        icon: 'organizations',
        href: '/manage-licenses'
      },
      // Admin contact on an Enterprise/institutional (non-self-serve) license.
      // Distinct from the Scite super-admin "Manage Licenses" link below: this
      // manages only their own org's license (SCI-3311). A Scite admin who is
      // also a license admin gets the plural super-admin link instead, not both.
      organizationLicense?.isEnterpriseAdmin &&
        !user?.admin && {
          name: 'Manage License',
          icon: 'organizations',
          href: '/manage-licenses'
        },
      user?.admin && {
        name: 'Admin',
        icon: 'edit',
        href: '/admin'
      },
      user?.admin && {
        name: 'Manage Licenses',
        icon: 'organizations',
        href: '/manage-licenses'
      },
      {
        name: 'Sign Out',
        icon: 'signOut',
        onClick: () => dispatch(logout(history)),
        href: '#'
      }
    ].filter(Boolean)

  const avatarItems = user ? setAvatarItems() : []

  const handleSignin = () => {
    dispatch(setMode('login'))
    dispatch(toggleModal('login'))
  }

  const handleSignup = () => {
    dispatch(setMode('register'))
    dispatch(toggleModal('login'))
  }

  //
  // Show loading indicator only if we expect a logged-in user
  //
  if (fetchingUser && hasToken) {
    return <CircleSpinner size={25} />
  }
  if (user?.email) {
    return (
      <NavDropdown
        dropdownClassName={classNames(styles.avatarWrapper, avatarWrapperMobile)}
        items={avatarItems}
        menuOffset={[-45, 12]}
      >
        <NavAvatar src={user.avatar} bg={avatarBg} />
      </NavDropdown>
    )
  }

  if (hideAuthButtons) return null

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSignup()
    }
  }

  const authButtons = showSignup ? (
    <div className={classNames(styles.buttonLayout, className, navBarButtons, loginClassName)}>
      <HoverLink onClick={handleSignin}>Log In</HoverLink>
      <Button
        className={`${styles.button} ${signupClassName}`}
        onClick={handleSignup}
        color={buttonColor}
        bold
        small
        onKeyDown={handleKeyDown}
      >
        Start Free Trial
      </Button>
    </div>
  ) : (
    <div className={styles.buttonLayout}>
      <Button className={styles.button} onClick={handleSignin} color={buttonColor} bold small>
        Log in
      </Button>
    </div>
  )
  return authButtons
}

export default NavLogin
